import React, { useState, useRef } from "react";
import {
  Modal,
  Box,
  Typography,
  Button,
  TextField,
  Paper,
  IconButton,
  Grid,
  Checkbox,
  FormControlLabel,
  MenuItem,
  Dialog,
  DialogContent,
  Divider,
} from "@mui/material";
import axios from "axios";
import { Snackbar, Alert } from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import ImageIcon from "@mui/icons-material/Image";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import VisibilityIcon from "@mui/icons-material/Visibility";
import AddIcon from "@mui/icons-material/Add";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { formatText } from "../../helper";

const BASE_URL = "https://one.ienergydigital.com/oneapp-backend";
const ORG_ID = 4;

const PROFICIENCY_LEVELS = ["beginner", "intermediate", "advanced", "expert"];
// new push
const JobApplicationModal = ({
  open,
  onClose,
  role,
  time,
  location,
  jobId,
  jobCode,
}) => {
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    mobileNumber: "",
    currentLocation: "",
    totalExperienceYears: "",
    linkedin: "",
  });

  const [skills, setSkills] = useState([
    { skill_name: "", proficiency_level: "beginner" },
  ]);

  const [consentAccepted, setConsentAccepted] = useState(false);

  const [files, setFiles] = useState([]);
  const fileInputRef = useRef(null);

  // Success popup ke liye — submit hone ke baad backend se aaya data yahan store hota hai
  const [successData, setSuccessData] = useState(null);
  const [copied, setCopied] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSkillChange = (index, field, value) => {
    setSkills((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const handleAddSkill = () => {
    setSkills((prev) => [
      ...prev,
      { skill_name: "", proficiency_level: "beginner" },
    ]);
  };

  const handleRemoveSkill = (index) => {
    setSkills((prev) => prev.filter((_, i) => i !== index));
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.type !== "application/pdf") {
      setSnackbar({
        open: true,
        message: "Only PDF files are allowed",
        severity: "error",
      });
      return;
    }

    setFiles([{ name: file.name, size: file.size, type: file.type, file }]);
  };

  const handleRemoveFile = (index) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleBrowseClick = () => {
    fileInputRef.current?.click();
  };

  const handleDragOver = (e) => e.preventDefault();

  const handleDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (!file) return;

    if (file.type !== "application/pdf") {
      setSnackbar({
        open: true,
        message: "Only PDF files are allowed",
        severity: "error",
      });
      return;
    }

    setFiles([{ name: file.name, size: file.size, type: file.type, file }]);
  };

  const handlePreview = (file) => {
    const fileUrl = URL.createObjectURL(file.file);
    window.open(fileUrl, "_blank");
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
    return (bytes / (1024 * 1024)).toFixed(1) + " MB";
  };

  const handleSubmit = async () => {
    try {
      if (!formData.fullName.trim()) {
        return setSnackbar({
          open: true,
          message: "Please enter full name",
          severity: "error",
        });
      }

      if (!formData.email.trim()) {
        return setSnackbar({
          open: true,
          message: "Please enter email",
          severity: "error",
        });
      }

      if (!formData.mobileNumber.trim()) {
        return setSnackbar({
          open: true,
          message: "Please enter mobile number",
          severity: "error",
        });
      }

      if (!files.length) {
        return setSnackbar({
          open: true,
          message: "Please upload resume PDF",
          severity: "error",
        });
      }

      if (!consentAccepted) {
        return setSnackbar({
          open: true,
          message: "Please accept consent to proceed",
          severity: "error",
        });
      }

      setLoading(true);

      const resumeFile = files[0].file;

      const validSkills = skills
        .filter((s) => s.skill_name.trim())
        .map((s) => ({
          skill_name: s.skill_name.trim(),
          proficiency_level: s.proficiency_level,
        }));

      const candidate = {
        full_name: formData.fullName,
        primary_email: formData.email,
        mobile_number: formData.mobileNumber,
        current_location: formData.currentLocation,
        total_experience_years: formData.totalExperienceYears
          ? Number(formData.totalExperienceYears)
          : 0,
        skills: validSkills,
      };

      const application = {
        source_code: "client-site-widget",
        utm_source: "careers_site",
        utm_medium: "web",
        notes: formData.linkedin
          ? `LinkedIn: ${formData.linkedin}`
          : "Submitted from embedded careers form",
      };

      const metadata = {
        sdk_client_id: "client-careers-web",
        page_url: typeof window !== "undefined" ? window.location.href : "",
      };

      // Build multipart form data instead of a JSON body.
      // The backend rejected base64 data URLs, so the resume must be sent
      // as a real binary file part (key: "resume_file"), not as JSON text.
      const fd = new FormData();
      fd.append("posting_code", jobCode);
      fd.append("consent_accepted", consentAccepted);
      fd.append("candidate", JSON.stringify(candidate));
      fd.append("application", JSON.stringify(application));
      fd.append("metadata", JSON.stringify(metadata));
      fd.append("answers", JSON.stringify([]));
      fd.append("resume_file", resumeFile, resumeFile.name);

      const response = await axios.post(
        `${BASE_URL}/public/recruitment/${ORG_ID}/career-page/applications`,
        fd
        // Do NOT manually set Content-Type here. Axios/the browser will
        // automatically set "multipart/form-data; boundary=..." for a
        // FormData body. Setting it yourself removes the boundary and
        // breaks parsing on the server.
      );

      const result = response?.data?.data;

      setSnackbar({
        open: true,
        message: response?.data?.message || "Successfully applied for the job",
        severity: "success",
      });

      setFormData({
        fullName: "",
        email: "",
        mobileNumber: "",
        currentLocation: "",
        totalExperienceYears: "",
        linkedin: "",
      });
      setSkills([{ skill_name: "", proficiency_level: "beginner" }]);
      setConsentAccepted(false);
      setFiles([]);

      // Form modal band karke success details popup dikhate hain,
      // taaki user application_code save kar sake
      if (result) {
        setSuccessData(result);
      } else {
        onClose();
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message:
          error?.response?.data?.message || "Failed to submit application",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const getFileIcon = (fileType) => {
    if (fileType.includes("image")) {
      return <ImageIcon sx={{ color: "#0066CC" }} />;
    }
    return <InsertDriveFileIcon sx={{ color: "#0066CC" }} />;
  };

  const handleCopyCode = () => {
    if (!successData?.application_code) return;
    navigator.clipboard.writeText(successData.application_code).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleSuccessClose = () => {
    setSuccessData(null);
    onClose();
  };

  return (
    <>
      <Modal
        open={open && !successData}
        onClose={onClose}
        aria-labelledby="job-application-modal"
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: "90%", sm: 500, md: 1000 },
            bgcolor: "background.paper",
            boxShadow: 24,
            borderRadius: 4,
            maxHeight: "90vh",
            overflow: "auto",
            p: 4,
          }}
        >
          <IconButton
            size="small"
            onClick={onClose}
            sx={{ position: "absolute", right: 12, top: 12 }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>

          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  mt: 1,
                  mb: 2,
                  borderStyle: "dashed",
                  borderColor: "#8C8CC2",
                  borderRadius: 3,
                  textAlign: "center",
                }}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 1,
                    width: "100%",
                  }}
                >
                  <CloudUploadIcon sx={{ fontSize: 40, color: "#0066CC" }} />
                  <Typography variant="body2" fontWeight="medium">
                    Choose a file or drag & drop it here
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    PDF format only.
                  </Typography>
                  <Button
                    variant="outlined"
                    onClick={handleBrowseClick}
                    sx={{
                      mt: 1,
                      textTransform: "none",
                      borderColor: "#E0E0E0",
                      color: "#0066CC",
                      "&:hover": {
                        borderColor: "#0066CC",
                        bgcolor: "transparent",
                      },
                    }}
                  >
                    Browse File
                  </Button>
                </Box>
              </Paper>

              {files?.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" fontWeight="medium" sx={{ mb: 1 }}>
                    File added
                  </Typography>
                  {files.map((file, index) => (
                    <Box
                      key={index}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        mb: 1,
                        borderBottom: "1px solid",
                        borderColor: "grey.200",
                        pb: 1,
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        {getFileIcon(file.type)}
                        <Typography variant="body2" sx={{ fontWeight: "medium" }}>
                          {file.name}
                        </Typography>
                      </Box>
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ mr: 1 }}
                        >
                          {formatFileSize(file.size)}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            color: "#0066CC",
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            mx: 1,
                          }}
                          onClick={() => handlePreview(file)}
                        >
                          <Box
                            component="span"
                            sx={{ display: "flex", alignItems: "center" }}
                          >
                            <VisibilityIcon sx={{ fontSize: 16, mr: 0.5 }} />
                            Preview
                          </Box>
                        </Typography>
                        <IconButton
                          size="small"
                          onClick={() => handleRemoveFile(index)}
                          sx={{ color: "grey.500" }}
                        >
                          <DeleteOutlineIcon fontSize="small" color="error" />
                        </IconButton>
                      </Box>
                    </Box>
                  ))}
                </Box>
              )}

              <Typography
                variant="body2"
                fontWeight="medium"
                sx={{ mb: 1, color: "#07329F" }}
              >
                Skills
              </Typography>
              {skills.map((skill, index) => (
                <Box
                  key={index}
                  sx={{ display: "flex", gap: 1, mb: 1, alignItems: "center" }}
                >
                  <TextField
                    size="small"
                    placeholder="Skill name"
                    value={skill.skill_name}
                    onChange={(e) =>
                      handleSkillChange(index, "skill_name", e.target.value)
                    }
                    sx={{ flex: 1 }}
                  />
                  <TextField
                    select
                    size="small"
                    value={skill.proficiency_level}
                    onChange={(e) =>
                      handleSkillChange(
                        index,
                        "proficiency_level",
                        e.target.value
                      )
                    }
                    sx={{ width: 140 }}
                  >
                    {PROFICIENCY_LEVELS.map((level) => (
                      <MenuItem key={level} value={level}>
                        {level}
                      </MenuItem>
                    ))}
                  </TextField>
                  <IconButton
                    size="small"
                    onClick={() => handleRemoveSkill(index)}
                    disabled={skills.length === 1}
                  >
                    <DeleteOutlineIcon fontSize="small" color="error" />
                  </IconButton>
                </Box>
              ))}
              <Button
                size="small"
                startIcon={<AddIcon />}
                onClick={handleAddSkill}
                sx={{ textTransform: "none" }}
              >
                Add Skill
              </Button>
            </Grid>

            <Grid item xs={12} md={6}>
              <Box sx={{ p: 3, position: "relative" }}>
                <Typography
                  variant="h6"
                  fontWeight="bold"
                  sx={{ fontSize: "20px" }}
                >
                  Apply for this role!
                </Typography>

                <Box sx={{ display: "flex", gap: 1, mt: 1, mb: 2, flexWrap: "wrap" }}>
                  <Typography
                    variant="body2"
                    sx={{ color: "#0066CC", fontWeight: "medium" }}
                  >
                    {formatText(role)}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    •
                  </Typography>
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    {formatText(time)}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    •
                  </Typography>
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    {formatText(location)}
                  </Typography>
                </Box>

                <Typography
                  variant="body2"
                  fontWeight="medium"
                  sx={{ mb: 1, color: "#07329F" }}
                >
                  Full Name
                </Typography>
                <TextField
                  fullWidth
                  name="fullName"
                  value={formData.fullName}
                  onChange={handleInputChange}
                  placeholder="Name of Candidate"
                  variant="outlined"
                  size="small"
                  sx={{ mb: 2 }}
                />

                <Typography
                  variant="body2"
                  fontWeight="medium"
                  sx={{ mb: 1, color: "#07329F" }}
                >
                  E-mail
                </Typography>
                <TextField
                  fullWidth
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="user@gmail.com"
                  variant="outlined"
                  size="small"
                  sx={{ mb: 2 }}
                />

                <Typography
                  variant="body2"
                  fontWeight="medium"
                  sx={{ mb: 1, color: "#07329F" }}
                >
                  Mobile Number
                </Typography>
                <TextField
                  fullWidth
                  name="mobileNumber"
                  value={formData.mobileNumber}
                  onChange={handleInputChange}
                  placeholder="+919999999999"
                  variant="outlined"
                  size="small"
                  sx={{ mb: 2 }}
                />

                <Typography
                  variant="body2"
                  fontWeight="medium"
                  sx={{ mb: 1, color: "#07329F" }}
                >
                  Current Location
                </Typography>
                <TextField
                  fullWidth
                  name="currentLocation"
                  value={formData.currentLocation}
                  onChange={handleInputChange}
                  placeholder="Bengaluru"
                  variant="outlined"
                  size="small"
                  sx={{ mb: 2 }}
                />

                <Typography
                  variant="body2"
                  fontWeight="medium"
                  sx={{ mb: 1, color: "#07329F" }}
                >
                  Total Experience (years)
                </Typography>
                <TextField
                  fullWidth
                  name="totalExperienceYears"
                  type="number"
                  value={formData.totalExperienceYears}
                  onChange={handleInputChange}
                  placeholder="4"
                  variant="outlined"
                  size="small"
                  sx={{ mb: 2 }}
                  inputProps={{ min: 0, step: 0.5 }}
                />

                <Typography
                  variant="body2"
                  fontWeight="medium"
                  sx={{ mb: 1, color: "#07329F" }}
                >
                  LinkedIn
                </Typography>
                <TextField
                  fullWidth
                  name="linkedin"
                  value={formData.linkedin}
                  onChange={handleInputChange}
                  placeholder="linkedin.com/in/user_name"
                  variant="outlined"
                  size="small"
                  sx={{ mb: 2 }}
                />

                <input
                  ref={fileInputRef}
                  accept="application/pdf"
                  type="file"
                  onChange={handleFileChange}
                  style={{ display: "none" }}
                />

                <FormControlLabel
                  control={
                    <Checkbox
                      checked={consentAccepted}
                      onChange={(e) => setConsentAccepted(e.target.checked)}
                    />
                  }
                  label={
                    <Typography variant="body2" color="text.secondary">
                      I consent to my data being processed for this application
                    </Typography>
                  }
                  sx={{ mb: 1 }}
                />

                <Button
                  fullWidth
                  variant="contained"
                  disableElevation
                  onClick={handleSubmit}
                  disabled={loading}
                  sx={{
                    mt: 1,
                    bgcolor: "#0A4CAD",
                    textTransform: "none",
                    "&:hover": {
                      bgcolor: "#083C8A",
                    },
                    borderRadius: 2,
                  }}
                >
                  {loading ? "Submitting..." : "Submit"}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Modal>

      <Dialog
        open={!!successData}
        onClose={handleSuccessClose}
        maxWidth="xs"
        fullWidth
      >
        <DialogContent sx={{ p: 4, textAlign: "center", position: "relative" }}>
          <IconButton
            size="small"
            onClick={handleSuccessClose}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>

          <CheckCircleIcon sx={{ fontSize: 56, color: "#2E7D32", mb: 1 }} />

          <Typography variant="h6" fontWeight="bold" sx={{ mb: 0.5 }}>
            Application Submitted!
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Please save your application code below to track your application
            status later.
          </Typography>

          <Box
            sx={{
              border: "1px dashed #0066CC",
              borderRadius: 2,
              p: 2,
              mb: 2,
              bgcolor: "#F5F9FF",
            }}
          >
            <Typography variant="caption" color="text.secondary">
              Application Code
            </Typography>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 1,
                mt: 0.5,
              }}
            >
              <Typography variant="h6" fontWeight="bold" sx={{ color: "#07329F" }}>
                {successData?.application_code}
              </Typography>
              <IconButton size="small" onClick={handleCopyCode}>
                <ContentCopyIcon fontSize="small" sx={{ color: "#0066CC" }} />
              </IconButton>
            </Box>
            {copied && (
              <Typography variant="caption" sx={{ color: "#2E7D32" }}>
                Copied to clipboard
              </Typography>
            )}
          </Box>

          <Divider sx={{ mb: 2 }} />

          <Box sx={{ textAlign: "left", mb: 3 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Candidate Code
              </Typography>
              <Typography variant="body2" fontWeight="medium">
                {successData?.candidate_code}
              </Typography>
            </Box>
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Typography variant="body2" color="text.secondary">
                Tracking Token
              </Typography>
              <Typography
                variant="body2"
                fontWeight="medium"
                sx={{ wordBreak: "break-all", textAlign: "right", ml: 2 }}
              >
                {successData?.public_tracking_token}
              </Typography>
            </Box>
          </Box>

          <Button
            fullWidth
            variant="contained"
            disableElevation
            onClick={handleSuccessClose}
            sx={{
              bgcolor: "#0A4CAD",
              textTransform: "none",
              "&:hover": { bgcolor: "#083C8A" },
              borderRadius: 2,
            }}
          >
            Done
          </Button>
        </DialogContent>
      </Dialog>

      <Snackbar
        open={snackbar?.open}
        autoHideDuration={3000}
        onClose={() =>
          setSnackbar((prev) => ({
            ...prev,
            open: false,
          }))
        }
        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <Alert severity={snackbar?.severity} variant="filled">
          {snackbar?.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default JobApplicationModal;